import type { Timestamp } from '@bufbuild/protobuf/wkt'

import type { SaveErrorReason } from '~/pages/Supply48Data/models/cellValue'
import { checkSavableStoredValue } from '~/pages/Supply48Data/models/cellValue'

/**
 * 1 行 (Leg × 受渡日) の編集状態。
 *
 * - slots: 48 コマの値 (index 0 = コマ番号 1)。undefined = 未変更 (元の値を使う)
 * - manualEditKomas: ユーザーが手動編集したコマ番号の Set (1-48)
 * - dirty: この行が保存待ちか
 *
 * updatedAt は editState から除外し dayIndex 経由で引く (§1 縮小)。
 */
export type EditRowState = {
  /** 編集中のスロット値 (index = komaNumber - 1)。 undefined は未変更 */
  slots: Array<number | null | undefined>
  /** 手動編集したコマ番号 (1-48) の Set */
  manualEditKomas: Set<number>
  /** 保存対象か */
  dirty: boolean
  /**
   * 行の legId (rowKey の逆引きを避けるために保持する)。
   * doSave での split('::') 逆パースを廃止するために entry に legId を持たせる。
   */
  legId: string
  /**
   * 行の受渡日。
   */
  deliveryDate: { year: number; month: number; day: number }
}

/** 行の識別キー: legId + deliveryDate (ISO 文字列) */
export type EditRowKey = string

/**
 * editRowKey を生成する。
 * legId + deliveryDate(YYYY-MM-DD) の組み合わせで一意にする。
 */
export function makeEditRowKey(
  legId: string,
  year: number,
  month: number,
  day: number,
): EditRowKey {
  return `${legId}::${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** 全編集状態の Map */
export type EditStateMap = Map<EditRowKey, EditRowState>

/**
 * セル値を更新し、 手動編集フラグを立てた新しい状態を返す。
 * autoSlots が存在する場合、 autoSlots の値と同じなら manualEditKomas から除去する
 * (= 自動計算値に戻した扱い)。
 */
export function updateSlot(
  prev: EditStateMap,
  key: EditRowKey,
  legId: string,
  deliveryDate: { year: number; month: number; day: number },
  komaNumber: number,
  newValue: number | null,
  autoSlotValue: number | null | undefined,
  /**
   * 手動変更の印を明示する。非定型の取引の列で、契約から作られた全コマ 0 の行に 0 を
   * 手で入れて保存した日を「入力済み」と区別するための印 (AC-S4D-060) で、自動計算値と
   * 同じ値でも付ける。省略時 (定型の取引の列) は自動計算値との一致で決める (従来どおり)。
   */
  markOverride?: boolean,
): EditStateMap {
  const next = new Map(prev)
  const existing = next.get(key)

  const prevState: EditRowState = existing ?? {
    slots: new Array<number | null | undefined>(48).fill(undefined),
    manualEditKomas: new Set<number>(),
    dirty: false,
    legId,
    deliveryDate,
  }

  const newSlots = [...prevState.slots]
  newSlots[komaNumber - 1] = newValue

  const newManualEditKomas = new Set(prevState.manualEditKomas)
  // 自動計算値に戻した場合は手動フラグを除去
  if (markOverride ?? newValue !== autoSlotValue) {
    newManualEditKomas.add(komaNumber)
  } else {
    newManualEditKomas.delete(komaNumber)
  }

  next.set(key, {
    ...prevState,
    slots: newSlots,
    manualEditKomas: newManualEditKomas,
    dirty: true,
  })
  return next
}

/**
 * 行の dirty / manualEditKomas を保存済みの値 (サーバー値) を基準に再計算する。
 *
 * updateSlot は 1 セルの書き込みごとに dirty を立て、自動計算値と異なれば手動編集
 * フラグを付ける「前向きの記録」しかしない。undo / Escape のように**元の値へ戻す**
 * 書き込みでも同じ記録が走るため、取り消したはずのセルが手動編集として保存され
 * (`buildSaveRows` の `manual_edit_komas`)、行も dirty のまま残って未保存ガードが
 * 誤発火する。書き込み後にこの関数を通すことで「サーバー値に戻ったセルは無かった
 * ことにする」を担保する (slots からも取り除き未編集状態へ畳む — 値を残すと
 * `isKomaManual` が「セッションで自動計算値に戻したコマ」と誤判定する)。
 *
 * @param serverSlots 保存済みの 48 コマ値 (index = コマ番号 - 1、未設定は null)
 * @param autoSlots 自動計算値 (未設定は undefined。手動編集フラグの判定基準)
 * @param savedManualKomas 非定型の取引の列でだけ渡す、保存済みの手動変更コマ番号
 *   (`day.manualEditKomas`)。渡した時は手動変更の印を自動計算値との一致ではなく
 *   `updateSlot` が付けた印 (state.manualEditKomas) のままにし、値が保存値と
 *   同じでも印の有無が保存済みと違えば dirty とする (AC-S4D-060: 0 → 0 の入力を
 *   保存対象にする)。定型の取引の列は渡さない (従来どおり)。
 */
export function recomputeRowState(
  state: EditRowState,
  serverSlots: Array<number | null>,
  autoSlots: Array<number | undefined>,
  savedManualKomas?: readonly number[],
): EditRowState {
  const manualEditKomas = new Set<number>()
  const slots = [...state.slots]
  let dirty = false

  for (const [index, value] of state.slots.entries()) {
    if (value === undefined) continue
    const serverValue = serverSlots[index] ?? null
    if (savedManualKomas !== undefined) {
      const koma = index + 1
      const marked = state.manualEditKomas.has(koma)
      if (value === serverValue && marked === savedManualKomas.includes(koma)) {
        // 値も印も保存済みと同じ = 編集なし (undo / Escape の取り消し)
        slots[index] = undefined
        continue
      }
      dirty = true
      if (marked) manualEditKomas.add(koma)
      continue
    }
    if (value === serverValue) {
      // 保存済みの値に戻ったセルは編集扱いしない (= undo / Escape の取り消し)
      slots[index] = undefined
      continue
    }
    dirty = true
    // 手動編集フラグの基準は自動計算値との一致 (updateSlot と同一規則)
    if (value !== autoSlots[index]) {
      manualEditKomas.add(index + 1)
    }
  }

  return { ...state, slots, manualEditKomas, dirty }
}

/**
 * コマが手動変更として扱われるかを判定する。
 * リセット対象件数 (AC-S4D-008) の判定規則。
 *
 * セッション中に値を書き込んだコマはセッション側のフラグが正 (自動計算値に戻した
 * コマはフラグが落ちるので、保存済みフラグが残っていても手動変更扱いしない)。
 * 書き込みが無いコマは保存済みフラグに従う。
 *
 * セルの視覚区別 (AC-S4D-049) は「編集中」と「変更後」を別レイヤとして区別するため、
 * この関数 (2 レイヤを 1 つに畳んだ判定) ではなく `isKomaEditing` / `isKomaSavedManual`
 * を使う (下記)。
 *
 * @param savedManualKomas 保存済みの手動変更コマ番号 (1-48)
 */
export function isKomaManual(
  editRow: EditRowState | undefined,
  savedManualKomas: readonly number[],
  komaNumber: number,
): boolean {
  if (editRow?.manualEditKomas.has(komaNumber) === true) return true
  // セッションで値を書いたのにフラグが無い = 自動計算値に戻したコマ
  if (editRow?.slots[komaNumber - 1] !== undefined) return false
  return savedManualKomas.includes(komaNumber)
}

/**
 * コマが「編集中（未保存の手動変更）」かを判定する (AC-S4D-049)。
 *
 * このセッションで書き込みがあり、かつ**保存済みの値と異なる**コマを対象にする
 * (design `48data.html:2182` の `applyValue`: `String(val) === orig` なら
 * `cell-edited` を外す、と同じ「保存済み値 (`dataset.original`) との差分」の定義。
 * 自動計算値との一致は見ない — 自動計算値に書き戻す途中の値でも、まだ保存して
 * いなければ「編集中」)。
 *
 * `editRow.slots[koma-1] !== undefined` だけで判定しない理由: 「自動計算に戻す」
 * ボタン (`revertRowToAuto`) は対象コマだけでなく行の全 48 コマへ自動計算値を
 * 書き込むため、 実際には値が変わらない (= 元々手動変更ではなかった) コマまで
 * `slots` が定義済みになる。 保存済み値との比較を挟むことで、 その「実質ノーオペ」
 * のコマまで編集中扱いにしてしまうことを避ける。
 *
 * 保存済みの手動変更 (`isKomaSavedManual`) とは独立したレイヤ。design
 * (`design/supply/48data.html` #4888 コメント) の `cell-edited` / `cell-manual` と
 * 同様、同じセルに両方付くことがある (= 既に保存済みの手動変更セルを、このセッションで
 * さらに上書き編集中の場合)。
 *
 * 保存に成功した行 (`dirty=false`) は、保存済みの値を取り直す前でも編集中にしない
 * (doSave は成功時に slots を残したまま dirty だけを落とす)。CSV ダウンロード確認の
 * 未保存コマ数 (`countUnsavedKomas`) もこの述語で数える。
 *
 * @param serverValue 保存済みの値 (`day.slots[koma-1]?.value ?? null`)
 * @param savedManualKomas 非定型の取引の列でだけ渡す、保存済みの手動変更コマ番号
 */
export function isKomaEditing(
  editRow:
    | Pick<EditRowState, 'slots' | 'dirty' | 'manualEditKomas'>
    | undefined,
  serverValue: number | null,
  komaNumber: number,
  savedManualKomas?: readonly number[],
): boolean {
  if (editRow?.dirty !== true) return false
  const edited = editRow.slots[komaNumber - 1]
  if (edited === undefined) return false
  if (edited !== serverValue) return true
  // 非定型の取引の列 (savedManualKomas を渡す列) は、値が同じでも手動変更の印が
  // 保存済みと違えば未保存 (AC-S4D-060、recomputeRowState と同じ規則)
  return (
    savedManualKomas !== undefined &&
    editRow.manualEditKomas.has(komaNumber) !==
      savedManualKomas.includes(komaNumber)
  )
}

/**
 * コマが「変更後（保存済みの手動変更）」かを判定する (AC-S4D-049)。
 *
 * サーバー側の保存済みフラグ (`day.manualEditKomas`) が無ければ即 false
 * (= このコマについて過去に保存された手動変更が無い。design の
 * `autoBase.has(k)` と同じ前提条件)。フラグがあれば、リセット対象件数
 * (AC-S4D-008) と同じ規則の `isKomaManual` にそのまま委ねる — 「変更後」は
 * 「保存済みの手動変更のうち、 まだ手動変更のままのもの」なので、 独自の
 * 比較 (自動計算値との差分の再計算) を持たない
 * (レビュー指摘 #9031: 別規則で判定し直すと、 呼び出し側の autoSlotValue の
 * 欠落値の扱い〈`?? null` vs `undefined`〉次第で `isKomaManual` と結果がずれ、
 * リセット件数には数えられているのに視覚的な印だけ消える不整合が起きていた。
 * `manualEditCount.ts` の「視覚区別とリセット件数は同じ判定を使うので一致する」
 * という不変条件も、 この統一で実際に成り立つ)。
 *
 * これにより、 保存済みの手動変更セルをこのセッションで自動計算値へ書き戻した
 * (= 「自動計算に戻す」操作、まだ保存していない) 場合は、 保存を待たずに
 * その場で印が消える (#5126 のリセット操作テストと同じ挙動。 `isKomaManual` の
 * 「セッションで値を書いたのにフラグが無い = 自動計算値に戻したコマ」規則が
 * そのまま効く)。
 *
 * @param savedManualKomas 保存済みの手動変更コマ番号 (1-48、`day.manualEditKomas`)
 */
export function isKomaSavedManual(
  editRow: EditRowState | undefined,
  savedManualKomas: readonly number[],
  komaNumber: number,
): boolean {
  if (!savedManualKomas.includes(komaNumber)) return false
  return isKomaManual(editRow, savedManualKomas, komaNumber)
}

/**
 * 指定行を「自動計算値に戻す」。
 * editState に行が無い (= 今セッション未編集) 場合も行を初期化してから revert する。
 * autoSlots が空 (全 undefined / null) の行は no-op。
 *
 * @param keptManualKomas revert 対象外 (GC ロック済み・過去日) で手動変更フラグを
 *   維持するコマ番号。値を据え置くコマのフラグまで落とすと保存時に
 *   `manual_edit_komas` から消えるため、呼び出し側で明示する。
 */
export function revertRowToAuto(
  prev: EditStateMap,
  key: EditRowKey,
  legId: string,
  deliveryDate: { year: number; month: number; day: number },
  autoSlots: Array<number | null | undefined>,
  keptManualKomas: Iterable<number> = [],
): EditStateMap {
  // autoSlots が空 (= 自動値なし) なら no-op
  const hasAuto = autoSlots.some((v) => v !== undefined && v !== null)
  if (!hasAuto) {
    return prev
  }
  const existing = prev.get(key)
  const base: EditRowState = existing ?? {
    slots: new Array<number | null | undefined>(48).fill(undefined),
    manualEditKomas: new Set<number>(),
    dirty: false,
    legId,
    deliveryDate,
  }
  const next = new Map(prev)
  next.set(key, {
    ...base,
    slots: [...autoSlots],
    manualEditKomas: new Set<number>(keptManualKomas),
    dirty: true,
  })
  return next
}

/**
 * 編集された (dirty=true) 行だけを抽出して返す。
 * 保存時のペイロードを構築する際に使う。
 */
export function getDirtyRows(
  editState: EditStateMap,
): Array<[EditRowKey, EditRowState]> {
  return Array.from(editState.entries()).filter(([, state]) => state.dirty)
}

/** セル単位の保存拒否マーク (`collectSaveErrorCells`) の Map キー。 */
export function makeSaveErrorCellKey(
  rowKey: EditRowKey,
  komaNumber: number,
): string {
  return `${rowKey}#${komaNumber}`
}

/**
 * 保存拒否になる 1 コマの生データ。バナー (index.tsx の `CellInputRejection`)
 * とセル単位のエラー印 (`collectSaveErrorCells`) が同じ走査を重複して持たない
 * よう、 `collectUnsavableCells` の結果をこの形で共有する
 * (レビュー指摘 #9031: 条件を変えた時に片方だけ直すとバナー件数とセル印が
 * ずれる、が理由)。
 */
export type UnsavableCellEntry = {
  legId: string
  deliveryDate: EditRowState['deliveryDate']
  /** コマ番号 - 1 (slots の index と同じ) */
  komaIdx: number
  /** 保存単位 (kWh/30min) の値 */
  stored: number
  reason: SaveErrorReason
}

/**
 * 保存を拒否されるコマを 1 箇所で走査する (AC-S4D-028 / AC-S4D-041 /
 * AC-S4D-047 / AC-S4D-049 共通の predicate)。 `checkSavableStoredValue` を
 * 呼ぶのはここだけにし、 バナー用の一覧 (index.tsx `toUnsavableRejections`)
 * とセル単位のエラー印 (`collectSaveErrorCells`) はどちらもこの結果から写す。
 *
 * **直前の保存試行の結果を表す** (slice #8988 の Constraints「エラーは直前の
 * 保存試行の拒否理由を状態源として持つ」)。呼び出し元 (index.tsx の
 * `handleSave`) が Save ボタン押下のたびに呼び直す契約にする — design
 * (`48data.html` の `_applySaveErrorMarks`) も保存試行のたびに全消し→再計算
 * している。
 *
 * **render のたびに (= 1 打鍵ごとに) 呼んではいけない**。`checkSavableStoredValue`
 * は「102」の入力途中の "1" のような未確定値も対象にするため、 打鍵のたびに
 * 呼ぶと入力中に誤ってエラー表示が点滅する。
 */
export function collectUnsavableCells(
  editState: EditStateMap,
): UnsavableCellEntry[] {
  const result: UnsavableCellEntry[] = []
  for (const row of editState.values()) {
    if (!row.dirty) continue
    for (const [komaIdx, stored] of row.slots.entries()) {
      // null (= 明示クリア) は checkSavableStoredValue も no-op (null を返す)
      // なので、 ここで先に弾いて stored を number へ narrow する。
      if (stored === undefined || stored === null) continue
      const reason = checkSavableStoredValue(stored)
      if (reason === null) continue
      result.push({
        legId: row.legId,
        deliveryDate: row.deliveryDate,
        komaIdx,
        stored,
        reason,
      })
    }
  }
  return result
}

/**
 * `collectUnsavableCells` の結果をセル単位のエラー印の Map (AC-S4D-049) に
 * 写す。 predicate の再評価はしない (呼び出し元が同じ `entries` をバナー用にも
 * 使う想定。 index.tsx の `handleSave` 参照)。
 */
export function collectSaveErrorCells(
  entries: readonly UnsavableCellEntry[],
): Map<string, SaveErrorReason> {
  const result = new Map<string, SaveErrorReason>()
  for (const entry of entries) {
    const rowKey = makeEditRowKey(
      entry.legId,
      entry.deliveryDate.year,
      entry.deliveryDate.month,
      entry.deliveryDate.day,
    )
    result.set(makeSaveErrorCellKey(rowKey, entry.komaIdx + 1), entry.reason)
  }
  return result
}

// ---------------------------------------------------------------------------
// updatedAt overlay 用型 (§4)
// ---------------------------------------------------------------------------

/**
 * 保存成功後の新 updatedAt を rowKey でキャッシュするオーバーレイ。
 * data refetch (query key 変化) 時にクリアする。
 * 実効 updatedAt = overlay.get(key) ?? dayIndex からの updatedAt。
 */
export type UpdatedAtOverlay = Map<EditRowKey, Timestamp | undefined>
